import { Controller } from "@hotwired/stimulus"

// A page with posts shown in the language they were written in, their
// translations asked for and not yet made (PiecesHelper#rendition, state
// "wanted"), looks in now and then until they arrive, and swaps each in as
// the page would have set it - so a reader who came by first waits a moment,
// and every reader after finds it done. Posts let in later (fresh_controller)
// are looked after too. After a while with nothing coming, the page lets the
// matter rest; the originals stand, and the note saying more is on its way
// comes off.
//
//   data-translations-url-value    where to ask (translations#index)
export default class extends Controller {
  static values = {
    url: String,
    every: { type: Number, default: 3000 },
    patience: { type: Number, default: 40 }
  }

  connect() {
    this.tries = 0
    this.seen = new Set()
    this.observer = new MutationObserver(() => this.newcomers())
    this.observer.observe(this.element, { childList: true, subtree: true })
    this.newcomers(1500)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.observer?.disconnect()
  }

  wanted() {
    return [...this.element.querySelectorAll("[data-translation-state='wanted']")]
  }

  // Posts wanting a translation the page has not asked about yet: on
  // arrival, and whenever more are let in. Asking starts over for them.
  newcomers(delay = 500) {
    const fresh = this.wanted().filter((el) => !this.seen.has(el.dataset.translationSlug))
    if (fresh.length === 0) return
    fresh.forEach((el) => this.seen.add(el.dataset.translationSlug))
    this.tries = 0
    this.schedule(delay)
  }

  schedule(delay = this.everyValue) {
    if (this.wanted().length === 0) return
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.look(), delay)
  }

  async look() {
    const wanted = this.wanted()
    if (wanted.length === 0) return
    if (document.visibilityState !== "visible") return this.schedule()

    if (++this.tries > this.patienceValue) return this.rest(wanted)

    const params = new URLSearchParams()
    wanted.slice(0, 40).forEach((el) => {
      params.append("slugs[]", el.dataset.translationSlug)
      if (el.dataset.translationExcerpt !== "true") params.append("full[]", el.dataset.translationSlug)
    })

    try {
      const response = await fetch(`${this.urlValue}?${params}`, { headers: { Accept: "application/json" } })
      if (response.ok) this.swapIn(await response.json())
    } catch { /* offline, perhaps; we ask again later */ }

    // Each arrival slows the asking a little; the rest wait their turn at the model.
    this.schedule(this.everyValue + Math.min(this.tries, 10) * 500)
  }

  // Each translated piece takes the place of the original, which is kept
  // inside it behind "show the original".
  swapIn(pieces) {
    for (const [slug, html] of Object.entries(pieces)) {
      const el = this.element.querySelector(`[data-translation-state='wanted'][data-translation-slug='${CSS.escape(slug)}']`)
      if (!el) continue
      const template = document.createElement("template")
      template.innerHTML = html.trim()
      const fresh = template.content.firstElementChild
      if (fresh) el.replaceWith(fresh)
    }
  }

  rest(wanted) {
    wanted.forEach((el) => {
      el.dataset.translationState = "original"
      el.querySelector(".rendition-note")?.remove()
    })
  }
}
